import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import ShowReviewsModal from '../components/ShowReviewsModal';
import { ServiceLabel } from '../contexts/StreamingServicesContext';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Heart, MessageCircle, Plus } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SocialFeed = ({ user, setUser }) => {
  const [feed, setFeed] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [activeView, setActiveView] = useState('following'); // 'following' or 'my-posts'
  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});
  const [comments, setComments] = useState({});
  const [addedShows, setAddedShows] = useState({});
  const [selectedShow, setSelectedShow] = useState(null);

  useEffect(() => {
    loadFeed();
    loadMyPosts();
    loadUserShows();
  }, []);

  const loadFeed = async () => {
    try {
      const response = await axios.get(`${API}/reviews/feed`, { withCredentials: true });
      setFeed(response.data);
    } catch (error) {
      console.error('Error loading feed:', error);
    }
  };

  const loadMyPosts = async () => {
    try {
      const response = await axios.get(`${API}/reviews/my-posts`, { withCredentials: true });
      setMyPosts(response.data);
    } catch (error) {
      console.error('Error loading my posts:', error);
    }
  };

  const loadUserShows = async () => {
    try {
      const response = await axios.get(`${API}/shows`, { withCredentials: true });
      const showTitles = {};
      response.data.forEach(show => {
        showTitles[show.title.toLowerCase()] = true;
      });
      setAddedShows(showTitles);
    } catch (error) {
      console.error('Error loading user shows:', error);
    }
  };

  const handleAddToWatchlist = async (showTitle, showId = null, streamingService = null, customService = null) => {
    try {
      const searchResponse = await axios.get(`${API}/search-tmdb`, {
        params: { q: showTitle },
        withCredentials: true
      });

      const tmdbData = searchResponse.data?.results?.[0] || {};
      
      await axios.post(
        `${API}/shows`,
        {
          title: showTitle,
          status: 'Want to Watch',
          streaming_service: streamingService,
          custom_service: customService,
          personal_rating: null,
          personal_review: null,
          image_url: tmdbData.poster_path ? `https://image.tmdb.org/t/p/w500${tmdbData.poster_path}` : null,
          backdrop_url: tmdbData.backdrop_path ? `https://image.tmdb.org/t/p/w780${tmdbData.backdrop_path}` : null,
          release_date: tmdbData.release_date || null,
          genres: tmdbData.genres || null,
          tmdb_id: tmdbData.tmdb_id || null,
          media_type: tmdbData.media_type || null
        },
        { withCredentials: true }
      );
      
      setAddedShows(prev => ({ ...prev, [showTitle.toLowerCase()]: true }));
      toast.success(`"${showTitle}" added to your Want to Watch list!`);
    } catch (error) {
      console.error('Error adding show:', error);
      toast.error('Error adding show to watchlist. Please try again.');
    }
  };

  const handleLike = async (postId, isLiked) => {
    try {
      if (isLiked) {
        await axios.delete(`${API}/reviews/${postId}/like`, { withCredentials: true });
      } else {
        await axios.post(`${API}/reviews/${postId}/like`, {}, { withCredentials: true });
      }
      loadFeed();
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const loadComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/reviews/${postId}/comments`, { withCredentials: true });
      setComments(prev => ({ ...prev, [postId]: response.data }));
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const toggleComments = async (postId) => {
    if (!showComments[postId]) {
      await loadComments(postId);
    }
    setShowComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  const handleAddComment = async (postId) => {
    const text = commentText[postId];
    if (!text?.trim()) return;

    try {
      await axios.post(
        `${API}/reviews/${postId}/comments`,
        { comment_text: text },
        { withCredentials: true }
      );
      setCommentText(prev => ({ ...prev, [postId]: '' }));
      await loadComments(postId);
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <SEO 
        title="Rated by the Flock - TV Show Reviews | baad.tv"
        description="See what your friends are watching! Read TV show reviews, ratings, and recommendations from your flock. Join the social TV community."
        url="/feed"
        type="website"
      />
      <div className="social-feed-container">
        <Navigation user={user} setUser={setUser} currentPage="feed" />
      
      <div className="feed-content">
        <div className="page-header">
          <h1 data-testid="feed-title">Raated by the Flock</h1>
          <p>See what BaadTV the flock is watching</p>
        </div>

        <div className="view-toggle">
          <Button
            variant={activeView === 'following' ? 'default' : 'outline'}
            onClick={() => setActiveView('following')}
            data-testid="following-toggle"
          >
            Following
          </Button>
          <Button
            variant={activeView === 'my-posts' ? 'default' : 'outline'}
            onClick={() => setActiveView('my-posts')}
            data-testid="my-posts-toggle"
          >
            My Posts
          </Button>
        </div>

        {(activeView === 'following' ? feed : myPosts).length === 0 ? (
          <Card className="empty-card">
            <CardContent className="empty-state">
              {activeView === 'following' ? (
                <>
                  <p data-testid="empty-feed-msg">No reviews yet. Follow friends to see their reviews!</p>
                  <Button onClick={() => window.location.href = '/friends'} className="mt-4">
                    Find Friends
                  </Button>
                </>
              ) : (
                <p data-testid="empty-my-posts-msg">You haven't shared any reviews yet!</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="feed-list">
            {(activeView === 'following' ? feed : myPosts).map(post => (
              <Card key={post.id} className="post-card" data-testid={`post-${post.id}`}>
                <CardContent className="post-content">
                  <div className="post-header">
                    <div className="user-info">
                      <Avatar>
                        <AvatarImage src={post.user.picture} alt={post.user.name} />
                        <AvatarFallback>{post.user.name[0]}</AvatarFallback>
                      </Avatar>
                      <div className="user-details">
                        <div className="user-name" data-testid={`post-author-${post.id}`}>
                          {post.user.username ? `@${post.user.username}` : post.user.name}
                        </div>
                        <div className="post-time">{formatDate(post.created_at)}</div>
                      </div>
                    </div>
                  </div>

                  <div className="post-body">
                    {post.show_image_url && (
                      <div 
                        className="show-poster-container clickable-poster"
                        onClick={() => setSelectedShow(post)}
                      >
                        <img src={post.show_image_url} alt={post.show_title} className="show-poster desktop-image" />
                        {post.backdrop_url && (
                          <img src={post.backdrop_url} alt={post.show_title} className="show-backdrop mobile-image" />
                        )}
                      </div>
                    )}
                    <div className="post-content-area">
                      <div className="show-title-row">
                        <h3 
                          className="show-title clickable-title" 
                          data-testid={`post-show-${post.id}`}
                          onClick={() => setSelectedShow(post)}
                        >
                          {post.show_title}
                        </h3>
                        {!addedShows[post.show_title.toLowerCase()] && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="add-to-watchlist-btn"
                            onClick={() => handleAddToWatchlist(post.show_title, post.show_id, post.streaming_service, post.custom_service)}
                            data-testid={`add-watchlist-${post.id}`}
                          >
                            <Plus className="icon-plus" />
                            Want to Watch
                          </Button>
                        )}
                      </div>
                      <div className="rating" data-testid={`post-rating-${post.id}`}>
                        <span className="rating-stars">⭐ {post.rating}/10</span>
                        {(post.streaming_service || post.custom_service) && (
                          <span className="streaming-service">
                            {' • '}<ServiceLabel name={post.custom_service || post.streaming_service} />
                          </span>
                        )}
                      </div>
                      <p className="review-text" data-testid={`post-review-${post.id}`}>{post.review_text}</p>
                    </div>
                  </div>

                  <div className="post-actions">
                    <Button
                      data-testid={`like-btn-${post.id}`}
                      variant="ghost"
                      size="sm"
                      className={`action-btn ${post.user_liked ? 'liked' : ''}`}
                      onClick={() => handleLike(post.id, post.user_liked)}
                    >
                      <Heart className={post.user_liked ? 'filled' : ''} />
                      <span>{post.like_count}</span>
                    </Button>
                    <Button
                      data-testid={`comment-btn-${post.id}`}
                      variant="ghost"
                      size="sm"
                      className="action-btn"
                      onClick={() => toggleComments(post.id)}
                    >
                      <MessageCircle />
                      <span>{post.comment_count}</span>
                    </Button>
                  </div>

                  {showComments[post.id] && (
                    <div className="comments-section" data-testid={`comments-${post.id}`}>
                      <div className="comments-list">
                        {comments[post.id]?.map(comment => (
                          <div key={comment.id} className="comment" data-testid={`comment-${comment.id}`}>
                            <Avatar className="comment-avatar">
                              <AvatarImage src={comment.user.picture} alt={comment.user.name} />
                              <AvatarFallback>{comment.user.name[0]}</AvatarFallback>
                            </Avatar>
                            <div className="comment-content">
                              <div className="comment-author">{comment.user.name}</div>
                              <div className="comment-text">{comment.comment_text}</div>
                              <div className="comment-time">{formatDate(comment.created_at)}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="add-comment">
                        <Textarea
                          data-testid={`comment-input-${post.id}`}
                          placeholder="Write a comment..."
                          value={commentText[post.id] || ''}
                          onChange={(e) => setCommentText(prev => ({ ...prev, [post.id]: e.target.value }))}
                          rows={2}
                        />
                        <Button
                          data-testid={`comment-submit-${post.id}`}
                          size="sm"
                          onClick={() => handleAddComment(post.id)}
                          disabled={!commentText[post.id]?.trim()}
                        >
                          Comment
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {selectedShow && (
          <ShowReviewsModal
            show={selectedShow}
            onClose={() => setSelectedShow(null)}
          />
        )}
      </div>

      <style jsx="true">{`
        .social-feed-container {
          min-height: 100vh;
          background: #121212;
        }

        .feed-content {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
        }

        .page-header {
          margin-bottom: 2.5rem;
        }

        .page-header h1 {
          font-size: 2.5rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .page-header p {
          color: #b0b0b0;
          font-size: 1.1rem;
        }

        .view-toggle {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 2rem;
        }

        .view-toggle :global(button) {
          flex: 1;
          max-width: 200px;
        }

        .view-toggle :global(button[variant="default"]) {
          background: #E62F88;
          color: #000000;
          border: none;
        }

        .view-toggle :global(button[variant="outline"]) {
          background: transparent;
          color: #b0b0b0;
          border: 1px solid #333333;
        }

        .view-toggle :global(button:hover) {
          opacity: 0.9;
        }

        .empty-card {
          border-radius: 8px;
          border: 1px solid #333333;
          background: #1a1a1a;
        }

        .empty-state {
          text-align: center;
          padding: 3rem;
          color: #b0b0b0;
        }

        .feed-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .post-card {
          border-radius: 8px;
          border: 1px solid #333333;
          background: #1a1a1a;
          transition: border-color 0.2s;
        }

        .post-card:hover {
          border-color: #F5C518;
        }

        .post-content {
          padding: 1.5rem !important;
        }

        .post-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.25rem;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .user-details {
          flex: 1;
          min-width: 0;
        }

        .user-name {
          font-weight: 600;
          color: #ffffff;
          font-size: 1rem;
        }

        .post-time {
          font-size: 0.85rem;
          color: #666;
        }

        .post-body {
          margin-bottom: 1rem;
          display: flex;
          gap: 1rem;
        }

        .show-poster-container {
          flex-shrink: 0;
          cursor: pointer;
        }

        .clickable-poster {
          transition: transform 0.2s;
        }

        .clickable-poster:hover {
          transform: scale(1.05);
        }

        .show-poster {
          width: 120px;
          height: 180px;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #333333;
        }

        .show-backdrop {
          width: 100%;
          height: auto;
          aspect-ratio: 16/9;
          object-fit: cover;
          border-radius: 8px;
          border: 1px solid #333333;
        }

        .desktop-image {
          display: block;
        }

        .mobile-image {
          display: none;
        }

        .post-content-area {
          flex: 1;
        }

        .show-title-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 0.75rem;
        }

        .show-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #ffffff;
          margin: 0;
          flex: 1;
        }

        .clickable-title {
          cursor: pointer;
          transition: color 0.2s;
        }

        .clickable-title:hover {
          color: #E62F88;
        }

        .add-to-watchlist-btn {
          flex-shrink: 0;
          white-space: nowrap;
          background: #E62F88 !important;
          color: #000000 !important;
          border: none !important;
        }

        .add-to-watchlist-btn:hover {
          background: #c92570 !important;
        }

        .icon-plus {
          width: 16px;
          height: 16px;
        }

        .rating {
          margin-bottom: 1rem;
        }

        .rating-stars {
          display: inline-block;
          padding: 0.35rem 0.85rem;
          background: #E62F88;
          color: #000000;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .streaming-service {
          color: #b0b0b0;
          font-size: 0.9rem;
        }

        .review-text {
          color: #b0b0b0;
          line-height: 1.7;
          font-size: 1rem;
        }

        .post-actions {
          display: flex;
          gap: 0.5rem;
          padding-top: 1rem;
          border-top: 1px solid #e9ecef;
        }

        .action-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: color 0.2s;
        }

        .action-btn.liked,
        .action-btn.liked:hover {
          color: #e74c3c;
        }

        .action-btn svg {
          width: 20px;
          height: 20px;
        }

        .action-btn .filled {
          fill: currentColor;
        }

        .comments-section {
          margin-top: 1.25rem;
          padding-top: 1.25rem;
          border-top: 1px solid #e9ecef;
        }

        .comments-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .comment {
          display: flex;
          gap: 0.75rem;
        }

        .comment-avatar {
          width: 36px;
          height: 36px;
          flex-shrink: 0;
        }

        .comment-content {
          flex: 1;
          background: #f8f9fa;
          padding: 0.75rem 1rem;
          border-radius: 12px;
        }

        .comment-author {
          font-weight: 600;
          color: #1a1a1a;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }

        .comment-text {
          color: #495057;
          font-size: 0.95rem;
          line-height: 1.5;
          margin-bottom: 0.25rem;
        }

        .comment-time {
          font-size: 0.8rem;
          color: #666;
        }

        .add-comment {
          display: flex;
          gap: 0.75rem;
          align-items: flex-end;
        }

        .add-comment textarea {
          flex: 1;
        }

        @media (max-width: 768px) {
          .social-feed-container {
            overflow-x: hidden;
          }

          .feed-content {
            padding: 1rem;
            padding-bottom: 100px;
            max-width: 100%;
            box-sizing: border-box;
          }

          .feed-list {
            gap: 1rem;
          }

          .post-card {
            max-width: 100%;
            box-sizing: border-box;
          }

          .post-content {
            padding: 1rem !important;
          }

          .page-header {
            margin-bottom: 1.5rem;
          }

          .page-header h1 {
            font-size: 2rem;
          }

          .view-toggle {
            gap: 0.5rem;
            margin-bottom: 1.5rem;
          }

          .view-toggle :global(button) {
            font-size: 0.9rem;
            padding: 0.5rem 1rem;
          }

          .post-body {
            flex-direction: column;
          }

          .desktop-image {
            display: none;
          }

          .mobile-image {
            display: block;
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }

          .show-backdrop {
            width: 100%;
            max-width: 100%;
            height: auto;
            aspect-ratio: 16/9;
            object-fit: cover;
          }

          .show-poster-container {
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }

          .show-title {
            font-size: 1.25rem;
          }

          .show-title-row {
            flex-direction: column;
            align-items: stretch;
          }

          .add-to-watchlist-btn {
            width: 100%;
            justify-content: center;
            margin-top: 0.5rem;
          }

          .user-info {
            width: 100%;
            display: flex !important;
            align-items: center !important;
          }

          .user-details {
            flex: 1 !important;
            min-width: 0 !important;
            display: block !important;
          }

          .user-name {
            display: block !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            font-size: 0.95rem !important;
            color: #ffffff !important;
          }

          .post-time {
            display: block !important;
            font-size: 0.8rem !important;
          }
        }
      `}</style>
      </div>
    </>
  );
};

export default SocialFeed;